// Cart-page engraving editor - a single shared popup (see
// snippets/cart-engraving-editor.liquid), opened and pre-filled for
// whichever line's "Edit" button was clicked (assets/cart.js's
// EngravingEditButton), instead of redirecting to the product page.
//
// Saving performs the cart mutation directly: remove the old line, then add
// a new one carrying the updated engraving properties merged into whatever
// other properties that line already had (gift wrap included), so nothing
// else about the line - gift wrap pairing, quantity, selling plan - is
// lost. Line item identity is tied to its properties in Shopify's cart, so
// there's no "edit properties in place" endpoint; this is the same
// remove-then-add approach used elsewhere in this theme for gift wrap and
// engraving removal (see cart.js's removeEngravingFromLine).
//
// The canvas preview logic below intentionally duplicates
// gift-options.js's engraving drawing code rather than importing it, so
// this file (and the cart page) doesn't need to load that file's Swiper
// dependency.
class CartEngravingEditor extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-cart-engraving-modal]');
    this.overlay = this.querySelector('[data-cart-engraving-overlay]');
    this.canvas = this.modal ? this.modal.querySelector('[data-engraving-canvas]') : null;
    this.canvasImage = null;
    this.canvasImageLoaded = false;
    this.lineInputs = this.modal ? Array.from(this.modal.querySelectorAll('[data-engraving-line-input]')) : [];
    this.styleButtons = this.modal ? Array.from(this.modal.querySelectorAll('[data-engraving-style-option]')) : [];
    this.styleName = this.modal ? this.modal.querySelector('[data-engraving-style-name]') : null;
    this.termsCheckbox = this.modal ? this.modal.querySelector('[data-engraving-terms-checkbox]') : null;
    this.saveButton = this.modal ? this.modal.querySelector('[data-cart-engraving-save]') : null;
    this.errorBox = this.modal ? this.modal.querySelector('[data-cart-engraving-error]') : null;
    this.feeContainer = this.modal ? this.modal.querySelector('[data-engraving-fee-container]') : null;
    this.feeTitle = this.modal ? this.modal.querySelector('[data-engraving-fee-title]') : null;
    this.feePrice = this.modal ? this.modal.querySelector('[data-engraving-fee-price]') : null;
    this.selectedStyle = 'block';
    // The line currently being edited: { lineKey, variantId, engravingId }.
    this.currentLine = null;

    if (!this.modal) return;

    this.relocateDrawer(this.modal, this.overlay);
    this.loadCanvasImage();
    this.loadCanvasFonts();

    window.addEventListener('resize', () => {
      if (this.modal.hasAttribute('open')) {
        this.resizeCanvas();
        this.drawPreview();
      }
    });

    this.lineInputs.forEach((input) => {
      input.addEventListener('input', () => {
        this.drawPreview();
        this.updateSaveState();
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.save();
        }
      });
    });

    this.styleButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectStyle(button));
    });

    if (this.termsCheckbox) this.termsCheckbox.addEventListener('change', () => this.updateSaveState());
    if (this.saveButton) this.saveButton.addEventListener('click', () => this.save());

    this.querySelectorAll('[data-cart-engraving-close]').forEach((element) => {
      element.addEventListener('click', () => this.close());
    });
  }

  loadCanvasImage() {
    if (!this.canvas) return;
    const src = this.canvas.dataset.engravingImage;
    if (!src) return;

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      this.canvasImage = image;
      this.canvasImageLoaded = true;
      this.drawPreview();
    };
    image.src = src;
  }

  loadCanvasFonts() {
    if (!document.fonts || !document.fonts.load) return;
    Promise.all([document.fonts.load('16px "Gideon Roman"'), document.fonts.load('16px "Monsieur La Doulaise"')])
      .then(() => this.drawPreview())
      .catch(() => {});
  }

  getCanvasFont(style) {
    if (style === 'roman') return '"Gideon Roman", Georgia, serif';
    if (style === 'cursive') return '"Monsieur La Doulaise", "Brush Script MT", cursive';
    return "'Montserrat', sans-serif";
  }

  resizeCanvas() {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = window.devicePixelRatio || 1;
    this.canvasWidth = rect.width;
    this.canvasHeight = rect.height;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);

    const ctx = this.canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  drawPreview() {
    if (!this.canvas) return;
    if (!this.canvasWidth) this.resizeCanvas();
    if (!this.canvasWidth) return;

    const ctx = this.canvas.getContext('2d');
    const width = this.canvasWidth;
    const height = this.canvasHeight;

    ctx.clearRect(0, 0, width, height);

    if (this.canvasImageLoaded && this.canvasImage) {
      const image = this.canvasImage;
      const imageRatio = image.width / image.height;
      const canvasRatio = width / height;

      const zoom = 1.6;
      let drawWidth;
      let drawHeight;

      if (imageRatio > canvasRatio) {
        drawHeight = height * zoom;
        drawWidth = drawHeight * imageRatio;
      } else {
        drawWidth = width * zoom;
        drawHeight = drawWidth / imageRatio;
      }

      const offsetX = (width - drawWidth) / 2;
      const offsetY = (height - drawHeight) / 2;
      ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
    }

    const placeholderText = this.canvas.dataset.engravingPlaceholder || 'Your text';
    const lines = this.lineInputs.map((input) => input.value.trim() || placeholderText);
    if (!lines.length) return;

    const fontSize = 19;
    const lineGap = 10;
    const lineHeight = fontSize + lineGap;
    const fontFamily = this.getCanvasFont(this.selectedStyle);

    ctx.fillStyle = '#C0C0C0';
    ctx.textBaseline = 'middle';
    ctx.font = `600 ${fontSize}px ${fontFamily}`;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';

    const totalHeight = lineHeight * (lines.length - 1);
    const startY = height / 2 - totalHeight / 2;

    const extraNudgeByStyle = { cursive: 8, roman: 8 };
    const horizontalNudge = 5 + (extraNudgeByStyle[this.selectedStyle] || 0);

    ctx.textAlign = 'center';
    lines.forEach((line, index) => {
      ctx.fillText(line, width / 2 + horizontalNudge, startY + index * lineHeight);
    });
  }

  selectStyle(button) {
    this.selectedStyle = button.dataset.engravingStyleOption;

    this.styleButtons.forEach((styleButton) => {
      const isSelected = styleButton === button;
      styleButton.classList.toggle('is-selected', isSelected);
      styleButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.styleName) {
      this.styleName.textContent = button.dataset.engravingStyleLabel || button.textContent.trim();
    }
    this.drawPreview();
  }

  updateSaveState() {
    if (!this.saveButton) return;
    const hasMessage = this.lineInputs.some((input) => input.value.trim() !== '');
    const termsAccepted = this.termsCheckbox ? this.termsCheckbox.checked : true;
    this.saveButton.disabled = !(hasMessage && termsAccepted);
  }

  showError(message) {
    if (!this.errorBox) return;
    if (!message) {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
      return;
    }
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  // Called by EngravingEditButton (cart.js) with that line's current
  // engraving data (rendered server-side onto the button, not read from
  // localStorage, so it's correct even if storage was cleared or this is a
  // different browser than the one that originally saved it).
  openForLine(line) {
    if (!this.modal) return;
    this.currentLine = line;
    this.currentFeeVariantId = line.feeVariantId;
    this.showError(null);

    if (this.feeContainer) {
      if (line.feeTitle && line.feePrice) {
        if (this.feeTitle) this.feeTitle.textContent = line.feeTitle;
        if (this.feePrice) this.feePrice.textContent = line.feePrice;
        this.feeContainer.hidden = false;
      } else {
        this.feeContainer.hidden = true;
      }
    }

    this.lineInputs.forEach((input, index) => {
      input.value = line[`line${index + 1}`] || '';
    });

    const savedStyle = line.style || 'block';
    const styleButton = this.styleButtons.find((button) => button.dataset.engravingStyleOption === savedStyle) || this.styleButtons[0];
    if (styleButton) this.selectStyle(styleButton);

    if (this.termsCheckbox) this.termsCheckbox.checked = false;
    this.updateSaveState();

    this.resizeCanvas();
    this.drawPreview();
    this.modal.show(this.lineInputs[0], this.lineInputs[0]);
  }

  close() {
    this.currentLine = null;
    if (this.modal) this.modal.hide();
  }

  save() {
    if (!this.currentLine) return;

    const values = this.lineInputs.map((input) => input.value.trim());
    const hasMessage = values.some((value) => value !== '');
    const termsAccepted = this.termsCheckbox ? this.termsCheckbox.checked : true;

    if (!hasMessage) {
      if (this.lineInputs[0]) this.lineInputs[0].focus();
      return;
    }
    if (!termsAccepted) {
      if (this.termsCheckbox) this.termsCheckbox.focus();
      return;
    }

    this.showError(null);
    if (this.saveButton) this.saveButton.disabled = true;

    const { lineKey, variantId, engravingId } = this.currentLine;
    const style = this.selectedStyle;

    fetch(`${routes.cart_url}.json`)
      .then((response) => response.json())
      .then((cart) => {
        const item = cart.items.find((cartItem) => cartItem.key === lineKey);
        if (!item) return Promise.reject(new Error('Line not found'));

        // Keep every other property this line already had (gift wrap
        // pairing included - see cart.js's getLinkedGiftWrapLine, which
        // matches by the _Gift Wrap Id *value*, not by line key, so
        // carrying it over here keeps that pairing intact even though the
        // line's key itself changes below) and only replace the
        // engraving-specific ones.
        const properties = { ...item.properties };
        ['Engraving Line 1', 'Engraving Line 2', 'Engraving Line 3', 'Engraving Style'].forEach(
          (key) => delete properties[key]
        );
        values.forEach((value, index) => {
          if (value) properties[`Engraving Line ${index + 1}`] = value;
        });
        properties['Engraving Style'] = style;
        properties['_Engraving Id'] = engravingId || `engraving-${Date.now()}`;

        // Remove the old line first, then add the replacement - unlike an
        // add-then-remove ordering, this stays correct even if the edited
        // text/style happens to hash back to the exact same line item key
        // (nothing is briefly double-counted in between).
        // ALSO remove any existing fee line associated with this engraving.
        const feeLine = cart.items.find(cartItem => cartItem.properties && cartItem.properties['_Linked Engraving Id'] === engravingId);
        
        // ALSO remove any existing gift wrap line associated with this item.
        const giftWrapId = item.properties && item.properties['_Gift Wrap Id'];
        const giftWrapLine = giftWrapId
          ? cart.items.find(
              (cartItem) =>
                cartItem.key !== lineKey &&
                cartItem.properties &&
                cartItem.properties['_Gift Wrap Id'] === giftWrapId &&
                cartItem.properties['_Gift Wrap Item'] === 'true'
            )
          : null;

        const updates = { [lineKey]: 0 };
        if (feeLine) updates[feeLine.key] = 0;
        if (giftWrapLine) updates[giftWrapLine.key] = 0;

        return fetch(`${window.Shopify.routes.root}cart/update.js`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates }),
        })
          .then((response) => response.json())
          .then(() => 
            fetch(`${routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                items: [
                  {
                    id: variantId,
                    quantity: item.quantity,
                    properties,
                    selling_plan: item.selling_plan_allocation
                      ? item.selling_plan_allocation.selling_plan.id
                      : undefined,
                  }
                ]
              })
            })
          )
          .then((response) => response.json())
          .then((addResponse) => {
            if (addResponse.status) {
              return Promise.reject(new Error(addResponse.description || addResponse.message));
            }

            const newFeeVariantId = this.currentFeeVariantId || '';
            if (!newFeeVariantId && !giftWrapLine) {
              return addResponse;
            }

            const addedVariantId = parseInt(variantId, 10);
            const parentItem = addResponse.items
              ? addResponse.items.find((i) => i.variant_id === addedVariantId || i.id === addedVariantId) || addResponse.items[0]
              : addResponse;
            const parentLineKey = parentItem ? parentItem.key : undefined;

            const childItemsToAdd = [];

            if (newFeeVariantId) {
              childItemsToAdd.push({
                id: parseInt(newFeeVariantId, 10),
                quantity: item.quantity,
                parent_line_key: parentLineKey || undefined,
                properties: {
                  '_Linked Engraving Id': properties['_Engraving Id'],
                  '_type': 'engraving-fee'
                }
              });
            }

            if (giftWrapLine) {
              childItemsToAdd.push({
                id: giftWrapLine.variant_id,
                quantity: item.quantity,
                parent_line_key: parentLineKey || undefined,
                properties: giftWrapLine.properties
              });
            }

            return fetch(`${routes.cart_add_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({ items: childItemsToAdd })
            }).then(res => res.json());
          })
          .then((addResponse) => {
            if (addResponse.status) {
              return Promise.reject(new Error(addResponse.description || addResponse.message));
            }

            try {
              localStorage.setItem(
                `engraving:${properties['_Engraving Id']}`,
                JSON.stringify({
                  line1: values[0] || '',
                  line2: values[1] || '',
                  line3: values[2] || '',
                  style,
                  updatedAt: Date.now(),
                })
              );
            } catch (error) {
              // Ignore - storage unavailable.
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-engraving-editor', cartData: addResponse });
            this.close();
          });
      })
      .catch((error) => {
        console.error(error);
        this.showError((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
      })
      .finally(() => {
        if (this.saveButton) this.saveButton.disabled = false;
      });
  }

  // Same fixed-position relocation trick as gift-options.js's drawers - see
  // that file for why.
  relocateDrawer(drawer, overlay) {
    if (!drawer || drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
    if (overlay) drawer.after(overlay);
  }
}

if (!customElements.get('cart-engraving-editor')) {
  customElements.define('cart-engraving-editor', CartEngravingEditor);
}
